import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeoMeta } from '../hooks/useSeoMeta';
import { useJsonLd } from '../hooks/useJsonLd';
import { useSmartAppStoreRedirect } from '../hooks/useSmartAppStoreRedirect';
import { buildIosExternalBrowserUrl } from '../utils/inAppBrowserEscape';
import { appLinksPageJsonLd } from '../seo/jsonLd';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AvatarStack from './home-redesign/AvatarStack';
import {
  APP_STORE_URL,
  INSTAGRAM_URL,
  PLAY_STORE_URL,
  TIKTOK_URL,
  UGC_CREATOR_URL,
} from './home-redesign/constants';
import { IconApple, IconGooglePlayMark } from './home-redesign/HomeRedesignIcons';
import { OG_IMAGE_URL } from './home-redesign/cityLinks';
import { useHomeRedesignStrings } from './home-redesign/useHomeRedesignStrings';
import { ReactComponent as NavLogo } from './home-redesign/assets/nav-logo.svg';

const storeBtnClass =
  'flex w-full h-[54px] items-center justify-center gap-2.5 rounded-[14px] bg-[#2a0f2a] px-4 font-jakarta text-white transition-opacity hover:opacity-90';

const secondaryBtnClass =
  'flex w-full items-center justify-center rounded-full border border-[rgba(42,15,42,0.12)] bg-white px-6 py-3.5 text-center font-jakarta text-[0.9375rem] font-semibold text-[#2a0f2a] shadow-[0px_1px_0px_rgba(0,0,0,0.02)] transition-colors hover:border-[rgba(74,33,71,0.2)]';

const socialBtnClass =
  'flex flex-1 min-h-[48px] items-center justify-center gap-2.5 rounded-full border border-[rgba(42,15,42,0.12)] bg-white px-4 font-jakarta text-sm font-semibold text-[#2a0f2a] shadow-[0px_1px_0px_rgba(0,0,0,0.02)] transition-colors hover:border-[rgba(74,33,71,0.2)]';

const AppLinks: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { nav, finalCta, appLinks } = useHomeRedesignStrings();
  const { mode, storeUrl, inAppBrowser, openStore } = useSmartAppStoreRedirect();
  const storeHref = useMemo(() => {
    if (!storeUrl || !inAppBrowser) return storeUrl;
    if (typeof navigator === 'undefined') return storeUrl;
    if (!/iPhone|iPad|iPod/i.test(navigator.userAgent)) return storeUrl;
    return buildIosExternalBrowserUrl(inAppBrowser, storeUrl);
  }, [inAppBrowser, storeUrl]);

  useSeoMeta({
    title: appLinks.seoTitle,
    description: appLinks.seoDescription,
    url: 'https://chisme.social/app',
    image: OG_IMAGE_URL,
    locale: currentLanguage === 'en' ? 'en' : 'es',
  });

  const jsonLd = useMemo(() => appLinksPageJsonLd(), []);
  useJsonLd(jsonLd);

  const handleStoreClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!storeUrl) return;
    event.preventDefault();
    openStore();
  };

  if (mode === 'redirecting') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fcf7fa] px-5 font-jakarta text-[#2a0f2a]">
        <p className="text-center text-lg font-medium">{appLinks.redirecting}</p>
      </div>
    );
  }

  if (mode === 'escape-prompt') {
    return (
      <div className="min-h-screen bg-[#fcf7fa] font-jakarta text-[#2a0f2a] antialiased">
        <main className="mx-auto flex min-h-screen w-full max-w-[400px] flex-col items-center justify-center gap-6 px-5 pb-10 pt-[max(2rem,env(safe-area-inset-top))] text-center">
          <Link to="/" className="text-[#EB42B2]" aria-label={nav.logoAria}>
            <NavLogo aria-hidden className="h-8 w-[102px]" />
          </Link>

          <div className="flex flex-col items-center gap-3">
            <h1 className="max-w-[18rem] font-instrument text-[1.75rem] italic leading-[1.15] tracking-[-0.02em] text-[#2a0f2a]">
              {appLinks.headline}
            </h1>
            <p className="max-w-[16rem] font-jakarta text-sm text-[#5a3158]">{appLinks.openInBrowserHint}</p>
          </div>

          {storeHref ? (
            <a
              href={storeHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`${storeBtnClass} max-w-[320px]`}
            >
              <IconApple className="size-6 shrink-0 text-white" />
              <span className="text-base font-bold tracking-[-0.01em]">{appLinks.openInStore}</span>
            </a>
          ) : null}

          <div className="mt-2 max-w-[18rem] rounded-2xl border border-[rgba(42,15,42,0.08)] bg-white/80 px-4 py-3 text-left">
            <p className="font-jakarta text-sm font-semibold text-[#2a0f2a]">{appLinks.openInSafariTitle}</p>
            <ol className="mt-2 space-y-1.5 font-jakarta text-sm text-[#5a3158]">
              <li>1. {appLinks.openInSafariStep1}</li>
              <li>2. {appLinks.openInSafariStep2}</li>
              <li>3. {appLinks.openInSafariStep3}</li>
            </ol>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcf7fa] font-jakarta text-[#2a0f2a] antialiased">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-48"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(255, 217, 238, 0.55) 0%, rgba(252, 247, 250, 0) 100%)',
        }}
        aria-hidden
      />

      <main className="relative mx-auto flex min-h-screen w-full max-w-[400px] flex-col px-5 pb-10 pt-[max(2rem,env(safe-area-inset-top))]">
        <div className="flex flex-col items-center gap-6 text-center">
          <Link to="/" className="text-[#EB42B2]" aria-label={nav.logoAria}>
            <NavLogo aria-hidden className="h-8 w-[102px]" />
          </Link>

          <div className="flex flex-col items-center gap-3">
            <h1 className="max-w-[18rem] font-instrument text-[1.75rem] italic leading-[1.15] tracking-[-0.02em] text-[#2a0f2a]">
              {appLinks.headline}
            </h1>

            <div className="flex items-center gap-2.5 rounded-full bg-white px-3 py-2 shadow-[0px_6px_10px_rgba(74,33,71,0.1)]">
              <AvatarStack size="sm" letters={['M', 'L', 'A', 'J']} />
              <div className="text-left font-jakarta text-xs leading-[13.2px] text-[#5a3158]">
                <p className="font-semibold text-[#2a0f2a]">{appLinks.socialLine1}</p>
                <p>{appLinks.socialLine2}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-6">
          <section aria-labelledby="app-links-download">
            <p
              id="app-links-download"
              className="mb-3 text-center font-jetbrains text-[10.5px] uppercase tracking-[0.22em] text-[#5a3158]"
            >
              {appLinks.downloadSection}
            </p>
            <div className="flex flex-col gap-2.5">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={storeBtnClass}
                onClick={handleStoreClick}
              >
                <IconApple className="size-6 shrink-0 text-white" />
                <span className="flex flex-col items-center leading-tight text-center">
                  <span className="text-[10px] font-medium opacity-75">{finalCta.downloadOn}</span>
                  <span className="text-base font-bold tracking-[-0.01em]">{finalCta.appStore}</span>
                </span>
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={storeBtnClass}
                onClick={handleStoreClick}
              >
                <IconGooglePlayMark className="size-6 shrink-0" />
                <span className="flex flex-col items-center leading-tight text-center">
                  <span className="text-[10px] font-medium opacity-75">{finalCta.getItOn}</span>
                  <span className="text-base font-bold tracking-[-0.01em]">{finalCta.googlePlay}</span>
                </span>
              </a>
            </div>
          </section>

          <section aria-labelledby="app-links-ugc">
            <p
              id="app-links-ugc"
              className="mb-3 text-center font-jetbrains text-[10.5px] uppercase tracking-[0.22em] text-[#5a3158]"
            >
              {appLinks.ugcSection}
            </p>
            <a
              href={UGC_CREATOR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={secondaryBtnClass}
            >
              {appLinks.ugcCreator}
            </a>
          </section>

          <section aria-labelledby="app-links-more">
            <p
              id="app-links-more"
              className="mb-3 text-center font-jetbrains text-[10.5px] uppercase tracking-[0.22em] text-[#5a3158]"
            >
              {appLinks.moreSection}
            </p>
            <div className="flex flex-col gap-2.5">
              <Link to="/" className={secondaryBtnClass}>
                {appLinks.visitHomepage}
              </Link>

              <Link id="app-links-explore" to="/ciudades" className={secondaryBtnClass}>
                {appLinks.explorePlans}
              </Link>
            </div>
          </section>

          <section aria-labelledby="app-links-follow">
            <p
              id="app-links-follow"
              className="mb-3 text-center font-jetbrains text-[10.5px] uppercase tracking-[0.22em] text-[#5a3158]"
            >
              {appLinks.followUs}
            </p>
            <div className="flex gap-2.5">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${socialBtnClass} hover:border-[#E1306C]/40`}
              >
                <svg className="size-5 shrink-0" viewBox="0 0 24 24" aria-hidden>
                  <defs>
                    <linearGradient id="app-links-ig" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FED373" />
                      <stop offset="25%" stopColor="#F15245" />
                      <stop offset="50%" stopColor="#D92E7F" />
                      <stop offset="75%" stopColor="#9B36B7" />
                      <stop offset="100%" stopColor="#515ECF" />
                    </linearGradient>
                  </defs>
                  <path
                    fill="url(#app-links-ig)"
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                  />
                </svg>
                <span>{appLinks.instagram}</span>
              </a>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${socialBtnClass} hover:border-[#010101]/25`}
              >
                <svg className="size-5 shrink-0" viewBox="0 0 24 24" aria-hidden>
                  <defs>
                    <linearGradient id="app-links-tt" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#25F4EE" />
                      <stop offset="50%" stopColor="#FE2C55" />
                      <stop offset="100%" stopColor="#000000" />
                    </linearGradient>
                  </defs>
                  <path
                    fill="url(#app-links-tt)"
                    d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"
                  />
                </svg>
                <span>{appLinks.tiktok}</span>
              </a>
            </div>
          </section>
        </div>

        <footer className="mt-auto flex flex-col items-center gap-4 pt-10 text-center">
          <LanguageSwitcher />
          <p className="font-jakarta text-sm text-[#5a3158]">{appLinks.madeWith}</p>
        </footer>
      </main>
    </div>
  );
};

export default AppLinks;
